import { useMemo, type ReactNode } from "react";
import { useTranslations } from "use-intl";
import { useQuery } from "@tanstack/react-query";

import { useTravellersSelectionContext } from "@/app/lib/context/TravellersSelectionContext";
import {
  allTravellerAgeGroups,
  type Traveller,
  type TravellerAgeGroup,
} from "@/app/lib/types/types";
import { TravellerSelectionButton } from "./TravellersSelectionButton";
import { travellersQueryOptions } from "@/app/lib/hooks/useTravellers";

type Props = {
  onSlotClick: (ageGroup: TravellerAgeGroup, index: number) => void;
  renderAdditionalTravellerControls?: (traveller: Traveller) => ReactNode;
};

export function TravellersSelection({
  onSlotClick,
  renderAdditionalTravellerControls,
}: Props) {
  const t = useTranslations();
  const { data: travellers } = useQuery(travellersQueryOptions());

  const travellersById = useMemo(() => {
    const map: Record<string, Traveller> = {};
    for (const traveller of travellers || []) {
      map[traveller.id] = traveller;
    }
    return map;
  }, [travellers]);

  const { travellerSlots } = useTravellersSelectionContext();

  return (
    <ul className="space-y-2">
      {allTravellerAgeGroups.map((ageGroup) =>
        travellerSlots[ageGroup].map((travellerId, index) => (
          <li key={`${ageGroup}-${index}`}>
            <TravellerSelectionButton
              traveller={travellerId ? travellersById[travellerId] : null}
              onClick={() => {
                onSlotClick(ageGroup, index);
              }}
            >
              {t("travellers.travellerTypeN", {
                type: ageGroup,
                number: index + 1,
              })}
            </TravellerSelectionButton>
            {travellerId
              ? renderAdditionalTravellerControls?.(travellersById[travellerId])
              : null}
          </li>
        )),
      )}
    </ul>
  );
}
